// Record construction + client-side CID precomputation (szzt-spec.md §4, §7.2).
//
// The deployment's `tile` strong ref must pin the manifest record's atproto CID, and both
// records are created in ONE atomic `applyWrites` (§7.1) — so the manifest-record CID (and,
// for the site pointer, the deployment-record CID) MUST be computed client-side before the
// commit. Record values are fully known at build time, so this is deterministic and needs no
// server round-trip.
//
// Encoding rules that make the precomputed CID match what a conforming PDS computes:
//   - the manifest record's `cid` is a PLAIN STRING — the upstream `ing.dasl.masl` lexicon declares
//     it `type: string, format: cid` (dasl.ing/tiles.html), and stock consumers (the appmosphe.re
//     tile index, `@dasl/tiles` loaders) read `typeof value.cid === "string"`; a `{$link}` cid-link
//     is silently unreadable to them. Records published before 2026-10 carry the link form —
//     readers accept both (`readCidField`), the write path never emits it.
//   - cid-link fields (`prev`, `root`) are written as `{ $link: <cid> }`; the dCBOR42
//     encoder (@atcute/cbor, via core `dagCbor`) emits them as tag-42 CIDs.
//   - strong-ref `cid` fields are LEXICON `format: cid` — plain CBOR text strings, NOT cid-links.
//   - optional fields are omitted (never null) when unset; the encoder drops `undefined`.
// records.record-cid.test.ts cross-checks these bytes against @ipld/dag-cbor (what the PDS uses).

import type { BuildResult } from "@szzt/core";
import type {
  DeploymentRecord,
  MaslRecord,
  SiteMeta,
  SiteRecord,
  StrongRef,
} from "./types.ts";

/** The three record collections (NSIDs) written on publish (§4). */
export { COLLECTION } from "@szzt/records";

/** Default entry document path (§4.2 `app.szzt.deployment.entry`). */
export const DEFAULT_ENTRY = "/index.html";

/** Build an `at://<did>/<collection>/<rkey>` URI. */
export function atUri(did: string, collection: string, rkey: string): string {
  return `at://${did}/${collection}/${rkey}`;
}

/** dCBOR42/DRISL CID of a fully-formed record value — the atproto CID the PDS will assign it. */
export { recomputeRecordCid as recordCid } from "@szzt/records";

/**
 * Assemble the `ing.dasl.masl` manifest record (§4.3). `tile` is the inline MASL from the build;
 * `cid` is its dCBOR42 root CID as a plain string (upstream lexicon `format: cid` — see the
 * encoding rules above). `prev` links the previous version's manifest record CID.
 */
export function buildMaslRecord(
  built: BuildResult,
  createdAt: string,
  prevManifestCid?: string,
): MaslRecord {
  const record: MaslRecord = {
    $type: "ing.dasl.masl",
    cid: built.rootCid,
    tile: built.manifest,
    createdAt,
  };
  if (prevManifestCid !== undefined) record.prev = { $link: prevManifestCid };
  return record;
}

/**
 * Assemble the `app.szzt.deployment` record (§4.2). `root` is the root MASL CID (cid-link);
 * `tile` is a strong ref pinning the manifest record's atproto CID; `site` back-references the
 * owning `app.szzt.site` rkey so a repo's deployments can be grouped by site for version history.
 */
export function buildDeploymentRecord(
  built: BuildResult,
  tile: StrongRef,
  entry: string,
  createdAt: string,
  site?: string,
): DeploymentRecord {
  const record: DeploymentRecord = {
    $type: "app.szzt.deployment",
    root: { $link: built.rootCid },
    entry,
    tile,
    createdAt,
  };
  if (site !== undefined) record.site = site;
  return record;
}

/**
 * Assemble the `app.szzt.site` record (§4.1). `current` is a strong ref to the active deployment.
 * Optional metadata is included only when provided.
 */
export function buildSiteRecord(
  current: StrongRef,
  createdAt: string,
  meta: SiteMeta = {},
): SiteRecord {
  const record: SiteRecord = {
    $type: "app.szzt.site",
    current,
    createdAt,
  };
  if (meta.title !== undefined) record.title = meta.title;
  if (meta.slug !== undefined) record.slug = meta.slug;
  if (meta.hints !== undefined) record.hints = meta.hints;
  return record;
}
